import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Container, Button, Spinner } from "reactstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { showToastMessage } from "../../../../common/service/CommonService";
import CommonForm from "Components/Common/CommonForm";
import { IP_STORAGE_PATHS } from "Routes/paths";
import FullPageLoader from "Components/Common/FullPageLoader";
import { useIpInventoryService } from "modules/Pharmacy/IPinventory/api/useIpInventoryService";
import { useLineClearanceService } from "modules/Pharmacy/LineClearance/api/useLineClearance";

import SectionCard from "Components/Common/SectionCard";
import { BreadcrumbContext } from "Layouts/Header";
import { useGlobalStudy } from "context/StudyContext";
import GlobalStudyMismatchWarning from "../../../../Components/Common/GlobalStudyMismatchWarning";

import StorageContainerSection from "../components/StorageContainerSection";
import {
  IpStorageFormValues,
  StorageContainerItem,
  getStudyProductFieldsRow1,
  getIpCategoryField,
  getBatchAndProductFields,
  getStorageDetailsFields,
} from "../helpers/ipstorage.constant";

const AddIpStorage: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const editId = paramId || searchParams.get("id");

  document.title = `${editId ? "Edit" : "Add"} IP Storage Record | ZENTIXS ORBIT`;

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "IP Storage", link: IP_STORAGE_PATHS.LIST },
      { text: editId ? "Edit IP Storage Record" : "Add IP Storage Record" },
    ]);
    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, [editId]);

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [containersList, setContainersList] = useState<StorageContainerItem[]>([]);
  const [containerError, setContainerError] = useState<string | null>(null);
  const validateContainersRef = useRef<(() => boolean) | null>(null);

//   const { InsertMutation, UpdateMutation, SelectIdQuery } = useIpStorageService();
//   const selectQuery = SelectIdQuery(editId ? Number(editId) : null);
  const { DropdownQuery: studySelectAllQuery } = useLineClearanceService({ getDropdown: true });

  const studyOptions = useMemo(
    () =>
      (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
        studyid: study.studyid,
        studycode: study.studycode,
        inventory: study.inventory || [],
        sponsorname: study.sponsorname
      })) || [],
    [studySelectAllQuery.data]
  );

  /* ------------------------------------------------------------------------ */
  /* 2. REACT HOOK FORM SETUP                                                 */
  /* ------------------------------------------------------------------------ */
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IpStorageFormValues>({
    defaultValues: {
      studycode: "",
      sponsorname: "",
      ipcategory: "",
      ipid: "",
      batchlotno: "",
      productname: "",
      manufacturingdate: "",
      expiryretestdate: "",
      storedfromdate: "",
      storedtodate: "",
      temperaturemonitoring: "",
      dataloggerid: "",
      storageremarks: "",
    },
    mode: "onChange",
  });

  const { selectedStudy: globalStudy } = useGlobalStudy();

  /* ------------------------------------------------------------------------ */
  /* 3. FORM WATCHERS & DERIVED STUDY OBJECT                                  */
  /* ------------------------------------------------------------------------ */
  const selectedStudyCode = watch("studycode");
  const selectedIpCategory = watch("ipcategory");
  const selectedIpId = watch("batchlotno");

  const selectedStudy = useMemo(
    () => studyOptions.find((s: any) => String(s.value) === String(selectedStudyCode)),
    [selectedStudyCode, studyOptions]
  );

  /* ------------------------------------------------------------------------ */
  /* 4. DYNAMIC IP CATEGORY OPTIONS FROM SELECTED STUDY                       */
  /* ------------------------------------------------------------------------ */
  const ipCategoryOptions = useMemo(() => {
    const inventory = selectedStudy?.inventory || [];
    return inventory.map((item: any) => ({
      label: item.ipcategory,
      value: item.ipcategory,
    }));
  }, [selectedStudy]);

  /* ------------------------------------------------------------------------ */
  /* 5. STUDY AUTO-FILL (from global study context)                           */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    if (editId) return;
    if (globalStudy?.studyid && studyOptions.length > 0) {
      const matched = studyOptions.find(
        (s: any) => String(s.studyid) === String(globalStudy.studyid)
      );
      if (matched) {
        console.log("matched : ",matched.sponsorname);
        setValue("studycode", matched.value, { shouldValidate: true, shouldDirty: true });
        setValue("sponsorname", matched.sponsorname, { shouldValidate: true, shouldDirty: true });
      }
    } else {
      setValue("studycode", "", { shouldValidate: true, shouldDirty: true });
      setValue("sponsorname", "");
    }
  }, [globalStudy?.studyid, studyOptions, editId, setValue]);

  // Reset dependent fields when study changes
  useEffect(() => {
    if (editId) return;
    setValue("ipcategory", "");
    setValue("productname", "");
    setValue("batchlotno", "");
    setValue("manufacturingdate", "");
    setValue("expiryretestdate", "");
    setValue("ipid", "");
    setContainersList([]);
    setContainerError(null);
  }, [selectedStudyCode, setValue, editId]);

  /* ------------------------------------------------------------------------ */
  /* 6. SELECT IP QUERY: Fetch IP detail & containers based on IP Category    */
  /* ------------------------------------------------------------------------ */
  const { SelectIdQuery: selectIpQuery } = useIpInventoryService({
    studyid: selectedStudy?.studyid || selectedStudyCode,
    ipcategory: selectedIpCategory,
  });

  const rawIpData = selectIpQuery.data?.data;
  const ipData = Array.isArray(rawIpData) ? rawIpData[0] : rawIpData;

  /* ------------------------------------------------------------------------ */
  /* 8. AUTO-FILL IP DETAILS & CONTAINERS ON IP CATEGORY / IP DATA LOAD     */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    if (editId) return;

    if (!selectedIpCategory || !ipData) {
      setValue("productname", "");
      setValue("batchlotno", "");
      setValue("manufacturingdate", "");
      setValue("expiryretestdate", "");
      setValue("ipid", "");
      setContainersList([]);
      return;
    }

    // Auto-fill IP details
    setValue("productname", ipData.productname || "", { shouldValidate: true });
    const batchVal = String(ipData.ipid || ipData.batchlotno || "");
    setValue("batchlotno", batchVal, { shouldValidate: true });
    setValue("manufacturingdate", ipData.manufacturingdate ? ipData.manufacturingdate.split("T")[0] : "", { shouldValidate: true });
    setValue(
      "expiryretestdate",
      ipData.expirydate
        ? ipData.expirydate.split("T")[0]
        : ipData.retestdate
        ? ipData.retestdate.split("T")[0]
        : "",
      { shouldValidate: true }
    );
    setValue("ipid", String(ipData.ipid || ""), { shouldValidate: true });

    // Populate containers from ipData
    const containers = ipData.containers || [];
    if (Array.isArray(containers) && containers.length > 0) {
      setContainersList(
        containers.map((c: any, idx: number) => ({
          ipdetailid: c.ipdetailid || c.containerid || idx + 1,
          containernumber: String(c.containernumber || idx + 1).padStart(2, "0"),
          containerquantity: c.containerquantity ?? c.initialquantity ?? c.quantity ?? 0,
          storagelocation: "",
          storageequipment: "",
          shelfrack: "",
          position: "",
          storagestatus: "Active",
          remarks: "",
        }))
      );
    } else {
      setContainersList([]);
    }
    setContainerError(null);
}, [ipData, selectedIpCategory, editId, setValue]);

//   useEffect(() => {
//     if (editId) return; // in edit mode containers come from the storage record itself (see below)
//     const record = (ipFullDetailQuery as any)?.data?.data?.[0];
//     if (record && Array.isArray(record.containers)) {
//       setContainersList(
//         record.containers.map((c: any, idx: number) => ({
//           ipdetailid: c.ipdetailid,
//           containernumber: c.containernumber || String(idx + 1).padStart(2, "0"),
//           containerquantity: c.initialquantity ?? "",
//           storagelocation: null,
//           storageequipment: null,
//           shelfrack: null,
//           position: null,
//           storagestatus: "Active",
//           remarks: null,
//         })),
//       );
//     } else {
//       setContainersList([]);
//     }
//     setContainerError(null);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [(ipFullDetailQuery as any)?.data, ipId, editId]);

  /* ------------------------------------------------------------------------ */
  /* EDIT MODE: populate everything (including containers) from storage record*/
  /* ------------------------------------------------------------------------ */
//   useEffect(() => {
//     const record = selectQuery.data?.data?.[0];
//     if (!record) return;

//     reset({
//       studycode: record.studyid,
//       sponsorname: record.sponsorname ?? "",
//       ipcategory: record.ipcategory ?? "",
//       ipid: record.ipid ?? "",
//       batchlotno: record.ipid ?? "",
//       productname: record.productname ?? "",
//       manufacturingdate: record.manufacturingdate ? record.manufacturingdate.split("T")[0] : "",
//       expiryretestdate: record.expiryretestdate ? record.expiryretestdate.split("T")[0] : "",
//       storedfromdate: record.storedfromdate ? record.storedfromdate.split("T")[0] : "",
//       storedtodate: record.storedtodate ? record.storedtodate.split("T")[0] : "",
//       temperaturemonitoring: record.temperaturemonitoring ?? "",
//       dataloggerid: record.dataloggerid ?? "",
//       storageremarks: record.storageremarks ?? "",
//     });

//     if (Array.isArray(record.containers)) {
//       setContainersList(
//         record.containers.map((c: any, idx: number) => ({
//           ipdetailid: c.ipdetailid,
//           containernumber: c.containernumber || String(idx + 1).padStart(2, "0"),
//           containerquantity: c.containerquantity ?? c.initialquantity ?? "",
//           storagelocation: c.storagelocation ?? null,
//           storageequipment: c.storageequipment ?? null,
//           shelfrack: c.shelfrack ?? null,
//           position: c.position ?? null,
//           storagestatus: c.storagestatus ?? "Active",
//           remarks: c.remarks ?? null,
//         })),
//       );
//     }
//   }, [selectQuery.data, reset, editId]);

  /* ------------------------------------------------------------------------ */
  /* STORAGE DETAILS SECTION WATCHERS                                         */
  /* ------------------------------------------------------------------------ */
  const storedFromDate = watch("storedfromdate");
  const temperatureMonitoring = watch("temperaturemonitoring");

  // Clear non-relevant field when temperature monitoring toggle flips
  useEffect(() => {
    if (temperatureMonitoring === "yes") {
      setValue("storageremarks", "");
    } else if (temperatureMonitoring === "no") {
      setValue("dataloggerid", "");
    }
  }, [temperatureMonitoring, setValue]);

  /* ------------------------------------------------------------------------ */
  /* FIELD CONFIGURATIONS                                                     */
  /* ------------------------------------------------------------------------ */
  const section1FieldsRow1 = getStudyProductFieldsRow1(studyOptions);
  const section1FieldsRow2 = getIpCategoryField(!selectedStudyCode || !!editId, ipCategoryOptions);
  const section1FieldsRow3 = getBatchAndProductFields();

  const section3Fields = getStorageDetailsFields(!selectedIpCategory, storedFromDate,temperatureMonitoring);

  /* ------------------------------------------------------------------------ */
  /* SUBMIT HANDLERS (UI mode without backend)                                */
  /* ------------------------------------------------------------------------ */

  //   const buildPayload = (data: any) => ({
  //   ipid: data.ipid,
  //   ipcategory: data.ipcategory,
  //   batchlotno: data.batchlotno,
  //   storedfromdate: data.storedfromdate,
  //   storedtodate: data.storedtodate,
  //   temperaturemonitoring: data.temperaturemonitoring,
  //   dataloggerid: data.temperaturemonitoring === "yes" ? data.dataloggerid : null,
  //   storageremarks: data.temperaturemonitoring === "no" ? data.storageremarks : null,
  //   containers: containersList.map((c) => ({
  //     ipdetailid: c.ipdetailid,
  //     containernumber: c.containernumber,
  //     storagelocation: c.storagelocation,
  //     storageequipment: c.storageequipment,
  //     shelfrack: c.shelfrack,
  //     position: c.position,
  //     storagestatus: c.storagestatus,
  //     remarks: c.remarks,
  //   })),
  // });
  const onSubmit = () => {
    if (validateContainersRef.current && !validateContainersRef.current()) return;

    if (editId) {
      setIsUpdateModalOpen(true);
    } else {
      showToastMessage("success", "IP Storage record saved successfully");
      navigate(IP_STORAGE_PATHS.LIST);
    }
  };
  // const payload = buildPayload(data);

    // if (editId) {
    //   setIsUpdateModalOpen(true);
    // } else {
    //   InsertMutation.mutate(payload, {
    //     onSuccess: (res: any) => {
    //       queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
    //       showToastMessage("success", res.message);
    //       navigate(IP_STORAGE_PATHS.LIST);
    //     },
    //     onError: (err: any) => showToastMessage("error", err),
    //   });
    // }

  const onInvalid = () => {
    if (validateContainersRef.current) validateContainersRef.current();
  };

  

  const handleUpdate = (_reasonPayload: ReasonSubmitPayload) => {
    showToastMessage("success", "IP Storage record updated successfully");
    setIsUpdateModalOpen(false);
    navigate(IP_STORAGE_PATHS.LIST);
  };

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>
        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          {/* Study Mismatch Warning */}
          <GlobalStudyMismatchWarning
            globalStudy={globalStudy}
            studyOptions={studyOptions}
            isLoaded={!studySelectAllQuery.isLoading}
          />

          {/* Section 1: Study & Product Details */}
          <SectionCard
            index={1}
            icon="ri-microscope-line"
            title="Study & Product Details"
            subtitle="Select the study and IP category to auto-fill product details and view containers for storage assignment."
          >
            <CommonForm fields={section1FieldsRow1} control={control} errors={errors} columns={2} />
            <div className="mt-3">
              <CommonForm fields={section1FieldsRow2} control={control} errors={errors} columns={1} />
            </div>
            <div className="mt-3">
              <CommonForm fields={section1FieldsRow3} control={control} errors={errors} columns={4} />
            </div>
          </SectionCard>

          {/* Section 2: Container Storage Details */}
          <SectionCard
            index={2}
            icon="ri-list-check-2"
            title="Container Storage Details"
            subtitle="Below are the containers available for the selected IP category. Enter storage location details for each container."
          >
            <StorageContainerSection
              containersList={containersList}
              setContainersList={setContainersList}
              containerError={containerError}
              setContainerError={setContainerError}
              selectedBatch={selectedIpCategory}
              onRegisterValidate={(fn) => {
                validateContainersRef.current = fn;
              }}
              isDisabled={!selectedIpCategory}
            />
          </SectionCard>

          {/* Section 3: Storage Details */}
          <SectionCard
            index={3}
            icon="ri-temp-cold-line"
            title="Storage Details"
            subtitle="Record the storage period and temperature monitoring requirement."
          >
            <CommonForm fields={section3Fields} control={control} errors={errors} columns={2} />
            {/* <div className="mt-3">
              {temperatureMonitoring === "yes" && (
                <CommonForm fields={dataLoggerField} control={control} errors={errors} columns={3} />
              )}
              {temperatureMonitoring === "no" && (
                <CommonForm fields={storageRemarksField} control={control} errors={errors} columns={3} />
              )}
            </div> */}
          </SectionCard>

          {/* Sticky Footer */}
          <div className={formStyles.stickyFooter}>
            <div className={formStyles.footerActions}>
              <Button
                type="button"
                color="dark"
                outline
                onClick={() => navigate(IP_STORAGE_PATHS.LIST)}
              >
                Cancel
              </Button>
              {/* <Button
                type="submit"
                color="primary"
                outline
                disabled={InsertMutation.isPending || UpdateMutation.isPending}
              >
                {InsertMutation.isPending && <Spinner size="sm" className="me-2" />} */}
              <Button type="submit" color="primary" outline>
                {editId ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </form>

        {isUpdateModalOpen && (
          <Reason
            isOpen={isUpdateModalOpen}
            toggle={() => setIsUpdateModalOpen(false)}
            click={handleUpdate}
            action="Update"
            id={editId ? Number(editId) : null}
            // isLoading={UpdateMutation.isPending}
          />
        )}
      </Container>
    </div>
  );
};

export default AddIpStorage;